import React from 'react'
import { AutoColumn } from 'components/Layout/Column'
import Row from 'components/Layout/Row'
import QuestionHelper from 'components/QuestionHelper'
import { useTranslation } from 'contexts/Localization'
import useTheme from 'hooks/useTheme'
import styled from 'styled-components'
import { Box, ChevronRightIcon, Text } from 'vvs-uikit'
import { useVaultsApr } from 'views/xVvs/hooks/useVaultsApr'
import { formatNumber } from 'utils/formatBalance'
import usePastWeekApr from 'views/xVvs/hooks/usePastWeekApr'
import LockPeriodLogo from '../../LockPeriodLogo'
import LockPeriodText from './LockPeriodText'

interface LockingPeriodRowProps {
  pid: number
  multiplier: number
  lockPeriod: number
  imgSrc: string
  onSelect: () => void
}

const StyledRow = styled(Row)`
  border-bottom: 2px solid ${({ color }) => color};
  padding: 24px 16px 24px 24px;

  &:nth-child(2) {
    border-top: 2px solid ${({ color }) => color};
  }

  &:hover {
    cursor: pointer;
  }
`

const StyledColumn = styled(AutoColumn)`
  display: none;

  ${({ theme }) => theme.mediaQueries.sm} {
    display: grid;
  }
`

const LockingPeriodRow = ({ pid, imgSrc, multiplier, lockPeriod, onSelect }: LockingPeriodRowProps) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const vaultsApr = useVaultsApr()
  const xvvsPastWeekApr = usePastWeekApr()
  const poolApr = vaultsApr.find((x) => x.poolId === pid)?.apr || '0'

  return (
    <StyledRow justify="space-between" color={theme.colors.backgroundAlt3} onClick={onSelect}>
      <Row align="center" color="text">
        <LockPeriodLogo imgSrc={imgSrc} width={42} height={42} />
        <LockPeriodText pid={pid} lockPeriod={lockPeriod} />
      </Row>
      <Row>
        <StyledColumn>
          <Row align="center">
            <Text color="textSubtle" small>
              {t('Multiplier')}
            </Text>
            <QuestionHelper
              text="This represents the multiplier factor applied to your staking amount. A 6x multiplier for example will yield 6 times the rewards and benefits compared to 1x."
              ml="6px"
              mt="4px"
            />
          </Row>
          <Text bold>{multiplier / 100}x</Text>
        </StyledColumn>
        <AutoColumn style={{ marginLeft: '40px', width: '120px' }}>
          <Row align="center">
            <Text color="textSubtle" small>
              {t('APR')}
            </Text>
            <QuestionHelper
              text={
                <Box>
                  <Text color="white" marginBottom="8px">
                    {t(
                      `Vault APRs are comprised of the auto compounded base APR from xVVS + the rewards from the VVS vault emission. xVVS base APR is realized once you unstake xVVS for VVS.`,
                    )}
                  </Text>
                  <Text color="white">{t(`xVVS APR: ${xvvsPastWeekApr.toFixed(3)}%`)}</Text>
                  <Text color="white">{t(`Vault APR: ${(Number(poolApr) - xvvsPastWeekApr).toFixed(3)}%`)}</Text>
                </Box>
              }
              ml="6px"
              mt="4px"
            />
          </Row>
          <Text bold width="128px" style={{ wordBreak: 'break-word' }}>
            {formatNumber(parseFloat(poolApr), 0, 2)}%
          </Text>
        </AutoColumn>
        <ChevronRightIcon color="primary" width="28px" />
      </Row>
    </StyledRow>
  )
}

export default LockingPeriodRow
